import { z } from 'zod';
import { Money } from './money';

/** Гостевая витрина: только то, что видно с порога. Никаких id-шников
 *  сверх нужного, никаких цен закупок — цены продажи не секрет. */
export const PublicMenu = z.object({
  place: z.string(),
  /** сегодня выходной: часы не врут, если заведение закрыто */
  closedToday: z.boolean().optional(),
  /** имя точки: в сети гость должен видеть, чьё меню открыл */
  locationName: z.string().optional(),
  /** режим работы для баннера «закрыто»; null — не показываем */
  hours: z.object({ open: z.string(), close: z.string() }).nullable(),
  /** возможности заведения: в магазине нет столов и официантов */
  features: z.object({ kitchen: z.boolean(), tables: z.boolean(), courses: z.boolean() }).optional(),
  /** акции, действующие ПРЯМО СЕЙЧАС — гостю, без расписаний */
  promoNow: z.array(z.string()).optional(),
  /** три хита за 28 дней — «часто заказывают», без цифр */
  topDishes: z.array(z.string()).optional(),
  /** «часто берут вместе»: имя блюда → имя пары (28 дней, ≥3 раз) */
  pairs: z.record(z.string(), z.string()).optional(),
  /** сколько гостей поставили 5★ — социальное доказательство */
  fiveStars: z.number().int().optional(),
  /** год основания — тёплая строка подвала */
  foundedYear: z.number().int().nullable().optional(),
  /** расписание недели текстом — разворот под «Открыто до» */
  hoursNote: z.string().nullable().optional(),
  /** медиана кухни за 28 дней: «обычно готовим ~N мин» */
  cookMins: z.number().int().optional(),
  /** зеркало busyNow: сейчас обычно свободно — приходите */
  quietNow: z.boolean().optional(),
  /** правда о часе: сейчас в заведении обычно людно */
  busyNow: z.boolean().optional(),
  /** контакты подвала: позвонить и доехать */
  contact: z.object({ phone: z.string().nullable(), address: z.string().nullable() }).nullable().optional(),
  /** ссылка «оставить на чай» — кнопка после оценки */
  tipUrl: z.string().nullable().default(null),
  /** имена топ-позиций сегодняшних продаж — бейдж «🔥» на витрине */
  hot: z.array(z.string()).default([]),
  categories: z.array(z.object({
    name: z.string(),
    products: z.array(z.object({
      name: z.string(),
      price: Money,
      unit: z.enum(['PIECE', 'KG', 'L', 'PORTION']),
      stopped: z.boolean(),
      /** блюду меньше 14 дней — «новинка» */
      isNew: z.boolean().optional(),
    /** фото блюда — ленивое, необязательное */
    photoUrl: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    badges: z.string().nullable().optional(),
    })),
  })),
});
export type PublicMenu = z.infer<typeof PublicMenu>;

/** Отзыв по QR с чека: один чек — один голос. */
export const PublicFeedback = z.object({
  locationId: z.string(),
  orderId: z.string(),
  rating: z.number().int().min(1).max(5),
  comment: z.string().max(300).optional(),
});
export type PublicFeedback = z.infer<typeof PublicFeedback>;

export const FeedbackRecent = z.object({
  avg: z.number().nullable(),
  count: z.number().int(),
  rows: z.array(z.object({
    rating: z.number().int(),
    comment: z.string().nullable(),
    at: z.string().datetime(),
  })),
});
export type FeedbackRecent = z.infer<typeof FeedbackRecent>;

/** Вызов со стола: официант или счёт. */
export const PublicCall = z.object({
  locationId: z.string(),
  tableId: z.string(),
  kind: z.enum(['WAITER', 'BILL']),
});
export type PublicCall = z.infer<typeof PublicCall>;

/** «Мой стол» на витрине: что уже заказано. */
export const PublicTableState = z.object({
  tableName: z.string(),
  order: z.object({
    number: z.number().int(),
    total: Money,
    items: z.array(z.object({ name: z.string(), qty: z.number() })),
  }).nullable(),
});
export type PublicTableState = z.infer<typeof PublicTableState>;

/** «Как в прошлый раз»: последний заказ гостя по телефону. */
export const PublicLastOrderReq = z.object({
  locationId: z.string(),
  phone: z.string().min(10).max(18),
});
export type PublicLastOrderReq = z.infer<typeof PublicLastOrderReq>;
export const PublicLastOrder = z.object({
  items: z.array(z.object({ name: z.string(), qty: z.number() })),
});
export type PublicLastOrder = z.infer<typeof PublicLastOrder>;

/** Предзаказ к открытию: текст и телефон, пока зал спит. */
export const PublicPreorder = z.object({
  locationId: z.string(),
  phone: z.string().min(10).max(18),
  text: z.string().min(3).max(300),
  /** «к 10:30» — необязательное время самовывоза */
  wishTime: z.string().max(20).optional(),
});
export type PublicPreorder = z.infer<typeof PublicPreorder>;
